class Depict {
  /**
   * Formats product from Depict API call.
   * @param {Object} product - Product object from Depict response
   * @returns {Object}
   */
  static formatDepictProduct (product) {
    const primaryVariant = product.variant_displays[product.variant_index]
    const productListingResultId = product.product_listing_result_id

    const handle = primaryVariant.slug

    const warehouseTags = window.theme?.warehouseTags || {}
    const marketHandle = window.theme?.depict?.marketHandle
    const productWarehouseTags =
      primaryVariant?.warehouse_tags || product?.warehouse_tags || []
    const matchedWarehouse = productWarehouseTags
      .map((gid) => warehouseTags[gid])
      .find((wh) => wh?.markets?.includes(marketHandle))
    const plpBadge = matchedWarehouse?.badge || ''

    const url =
      window.Shopify.routes.root === '/'
        ? `//${location.host}/products/${handle}`
        : `//${location.host}${window.Shopify.routes.root}products/${handle}`

    const formattedProduct = {
      available: primaryVariant.in_stock,
      compareAtPrice: {
        amount: Number(primaryVariant.original_price) * 100,
        currencyCode: window.theme.depict.currency,
      },
      description: '',
      handle,
      id: primaryVariant.item_group_id,
      variantId: primaryVariant.store_product_id,
      productListingResultId,
      price: {
        amount: Number(primaryVariant.sale_price) * 100,
        currencyCode: window.theme.depict.currency,
      },
      tags: primaryVariant.tags,
      title: primaryVariant.title.split(' - ')[0],
      url,
      media: Depict.formatDepictMedia(
        primaryVariant.image_urls,
        primaryVariant.title,
      ),
      variants: Depict.formatDepictVariant(product.variant_displays),
      hideOnCollections: Depict.formatCollectionHandles(
        primaryVariant?.product_hide_on_collections,
      ),
      flatImageCollections: Depict.formatCollectionHandles(
        primaryVariant?.flat_image_show_on_collections,
      ),
      featuredImageIsFlat: primaryVariant?.featured_image_is_flat,
      plpBadge,
    }

    formattedProduct.featuredImage = formattedProduct.media[0]

    return formattedProduct
  }

  /**
   * Formats variants.
   * @param {Array} variants - Depict product variants.
   * @returns {Array}
   */
  static formatDepictVariant (variants) {
    return variants.map((variant) => {
      const formattedVariant = {
        available: variant.in_stock,
        id: variant.product_id,
        variantId: variant.store_product_id,
        selectedOptions: variant.selected_options,
        title: variant.variant_title || variant.title,
      }

      return formattedVariant
    })
  }

  /**
   * Formats media.
   * @param {Array} media - Depict product media.
   * @returns {Array}
   */
  static formatDepictMedia (media, productTitle) {
    return media.map((item) => {
      const formattedMedia = {
        alt: productTitle,
        src: item,
        media_type: 'image',
      }

      return formattedMedia
    })
  }

  /**
   * Formats filters from Depict API call.
   * @param {Array} filters - Depict filters.
   * @returns {Array}
   */
  static formatDepictFilters (filters, selectedFilters) {
    return filters?.map((filter) => {
      const selectedFilter = selectedFilters.find(
        (sf) => sf.key === filter.field,
      )

      const options =
        filter.meta.values?.map((value, index) => ({
          value,
          name: filter.meta.names[index],
          count: filter.meta.counts[index],
          selected: selectedFilter?.values.includes(value),
        })) || []

      const formattedFilter = {
        id: filter.id,
        key: filter.field,
        name: filter.meta?.group_title,
        op: filter.op,
        options,
      }

      if (filter.meta?.type === 'range') {
        formattedFilter.min = filter.meta.min
        formattedFilter.max = filter.meta.max
      }

      return formattedFilter
    })
  }

  /**
   * Formats collection handles if they are separated by a comma character.
   * @param {Array} collections - Shopify collection handles
   * @returns {Array}
   */
  static formatCollectionHandles (collections) {
    if (!collections) {
      return []
    }

    const formattedHandles = collections.reduce((acc, collection) => {
      if (collection.includes(',')) {
        const splitCollections = collection.split(',')

        acc.push(...splitCollections)
      } else {
        acc.push(collection)
      }
      return acc
    }, [])

    return formattedHandles
  }

  static depictProductCard ({
    formattedProduct,
    index,
    showFlatImages,
    disableTag = '',
  }) {
    const featuredImage =
      showFlatImages && formattedProduct.featuredImageIsFlat === false
        ? formattedProduct.media[formattedProduct.media.length - 1]
        : formattedProduct.media[0]
    const hoverImage =
      showFlatImages && formattedProduct.featuredImageIsFlat === false
        ? formattedProduct.media[0]
        : formattedProduct.featuredImageIsFlat === true
          ? formattedProduct.media[1]
          : formattedProduct.media[formattedProduct.media.length - 1]

    const mobileMedia = formattedProduct.media.map((media, index) => {
      return media === featuredImage
        ? ''
        : `<a
            class="prd-Card_GallerySlide"
            data-mobile-product-gallery-el="slide"
            href="${formattedProduct.url}"
          >
            ${Depict.responsiveImage({ image: media, viewport: 'mobile' })}
          </a>`
    })

    const customTag = Depict.getCustomTag(formattedProduct, disableTag)
    const badge = Depict.getBadge(formattedProduct)

    return `<product-card
            class="prd-Card"
            data-product-url="${formattedProduct.url}"
            data-id="${formattedProduct.id}"
            data-handle="${formattedProduct.handle}"
            data-price="${formattedProduct.price.amount}"
            data-currency="${formattedProduct.price.currencyCode}"
            data-title="${formattedProduct.title}"
          >
          <article class="prd-Card_Inner util-FauxLink">
            <div class="prd-Card_MediaContainer">
              <div class="prd-Card_Media prd-Card_Media-desktop">
                <div data-product-card-el="colours"></div>

                ${
                  featuredImage
                    ? `<div class="prd-Card_Image prd-Card_FeatureImage">
                    ${Depict.responsiveImage({ image: featuredImage, loading: index < 4 ? 'eager' : 'lazy', viewport: 'desktop' })}
                  </div>`
                    : ''
                }

                ${
                  hoverImage
                    ? `<div class="prd-Card_Image prd-Card_HoverImage">
                    ${Depict.responsiveImage({ image: hoverImage, viewport: 'desktop' })}
                  </div>`
                    : ''
                }
              </div>

              <div class="prd-Card_Media prd-Card_Media-mobile">
                <mobile-product-gallery class="prd-Card_Gallery">
                  <div class="prd-Card_GalleryInner">
                    ${
                      featuredImage
                        ? `<a
                        class="prd-Card_GallerySlide"
                        data-mobile-product-gallery-el="slide"
                        href="${formattedProduct.url}"
                      >
                        ${Depict.responsiveImage({ image: featuredImage, viewport: 'mobile' })}
                      </a>`
                        : ''
                    }

                    ${mobileMedia.filter(Boolean).join('')}
                  </div>

                  <div class="prd-Card_GalleryPagination">
                    <ul class="prd-Card_GalleryPaginationList">
                      ${formattedProduct.media
                        .map((media, index) => {
                          return `<li
                            class="prd-Card_GalleryPaginationItem"
                            data-mobile-product-gallery-el="indicator"
                            data-index="${index}"
                          >
                            <span class="util-ScreenReaderOnly">${formattedProduct.title}</span>
                          </li>`
                        })
                        .filter(Boolean)
                        .join('')}
                    </ul>
                  </div>
                </mobile-product-gallery>
              </div>

              ${
                badge
                  ? `
                  <div class="prd-Card_Badges">
                    <span class="prd-Card_Badge fz-10_155">${badge}</span>
                  </div>
                `
                  : ''
              }

              ${
                customTag
                  ? `
                  <div class="prd-Card_Tags">
                    <span class="prd-Card_Tag fz-10_155">${customTag}</span>
                  </div>
                `
                  : ''
              }
            </div>

            <div class="prd-Card_Footer">
              <div class="prd-Card_Body">
                <p class="prd-Card_Title fz-12_155" id="ProductCard-${formattedProduct.productListingResultId}">
                  ${formattedProduct.title}
                </p>

                <div class="prd-Card_Hover" data-product-card-el="variants"></div>
              </div>

              <div class="prd-Card_Price fz-12_155">
                ${
                  formattedProduct.compareAtPrice.amount >
                  formattedProduct.price.amount
                    ? `<s class="prd-Price_RegularPrice">
                        ${window.BAO.Utils.formatMoney(formattedProduct.compareAtPrice.amount).replace('.', ' ').replace(',', ' ')} ${formattedProduct.price.currencyCode}
                      </s>

                      <span class="prd-Price_SalePrice">
                        ${window.BAO.Utils.formatMoney(formattedProduct.price.amount).replace('.', ' ').replace(',', ' ')} ${formattedProduct.price.currencyCode}
                      </span>`
                    : `${window.BAO.Utils.formatMoney(formattedProduct.price.amount).replace('.', ' ').replace(',', ' ')} ${formattedProduct.price.currencyCode}`
                }
              </div>

              <div class="prd-Card_Colors" data-product-card-el="colours-footer"></div>
            </div>

            <a class="util-FauxLink_Link" href="${formattedProduct.url}" data-product-listing-result-id="${formattedProduct.productListingResultId}">
              <span class="util-ScreenReaderOnly">${formattedProduct.title}</span>
            </a>
          </article>
        </product-card>`
  }

  static getBadge (product) {
    return product?.plpBadge || ''
  }

  static getCustomTag (product, disableTag) {
    const customTag = product?.tags?.find((tag) => tag.startsWith('custom:'))
    const customTagValue = customTag ? customTag.split('custom:')[1] : ''

    return customTagValue !== disableTag ? customTagValue : ''
  }

  static responsiveImage ({ image, loading = 'lazy', viewport }) {
    return `<img src="${image.src}&amp;width=250" alt="${image.alt}" srcset="${image.src}&amp;width=180 180w, ${image.src}&amp;width=600 600w, ${image.src}&amp;width=700 700w, ${image.src}&amp;width=800 800w, ${image.src}&amp;width=900 900w, ${image.src}&amp;width=1000 1000w" class="rsp-Image_Image" sizes="(max-width: 767px) ${viewport === 'mobile' ? '100vw' : '0vw'}, (min-width: 768px) ${viewport === 'desktop' ? 'calc(100vw / 4)' : '50vw'}" width="1000" height="1250" decoding="async" loading="${loading}">`
  }

  static productVariantSorted (product) {
    const textSizes = [
      'XXXS',
      'XXS',
      'XS',
      'XS/S',
      'S',
      'S/M',
      'M',
      'M/L',
      'L',
      'L/XL',
      'XL',
      'XXL',
      'XXXL',
      'T1',
      'T2',
      'T3',
      'T4',
      'T5',
    ]
    const variants = product?.variants

    const sortedVariants = variants.sort((a, b) => {
      const aSize = a.title
      const bSize = b.title

      // Check for S-suffix sizes like "28S", "30S", etc.
      const aSMatch = aSize.match(/^(?<number>\d+)S$/)
      const bSMatch = bSize.match(/^(?<number>\d+)S$/)

      if (aSMatch && bSMatch) {
        // Compare numerically if both are in the format "28S", "30S", etc.
        return (
          parseInt(aSMatch.groups.number) - parseInt(bSMatch.groups.number)
        )
      }
      if (aSMatch) {
        // Place S-suffix sizes after numeric sizes but before text sizes
        return isNaN(bSize) ? -1 : 1
      }
      if (bSMatch) {
        // Place S-suffix sizes after numeric sizes but before text sizes
        return isNaN(aSize) ? 1 : -1
      }

      // Check if both sizes are jean sizes in the format "waist/inseam"
      const aIsJeanSize = aSize.includes('/') && aSize.split('/').length === 2
      const bIsJeanSize = bSize.includes('/') && bSize.split('/').length === 2

      if (aIsJeanSize && bIsJeanSize) {
        const [aWaist, aInseam] = aSize.split('/').map(Number)
        const [bWaist, bInseam] = bSize.split('/').map(Number)

        // Compare by waist size first, then by inseam size
        if (aWaist !== bWaist) {
          return aWaist - bWaist
        }
        return aInseam - bInseam
      }

      // If both sizes are numbers (but not jean sizes), compare numerically
      if (!isNaN(aSize) && !isNaN(bSize)) {
        return parseFloat(aSize) - parseFloat(bSize)
      }

      if (typeof aSize === 'string' && typeof bSize === 'string') {
        const aIndex = textSizes.indexOf(aSize)
        const bIndex = textSizes.indexOf(bSize)

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex
        }
      }

      return 0
    })
    return sortedVariants
  }

  static depictFilters ({ filters, expandedFilters, format = 'collection' }) {
    const filtersHTML = filters?.map((filterData) => {
      const filterOptions = filterData.options.map((filterOption) => {
        return `
          <li class="drw-Filters_Option">
            <input
              id="${filterData.key}-${filterOption.value}"
              class="drw-Filters_Input util-ScreenReaderOnly"
              type="checkbox"
              ${filterOption.selected ? 'checked' : ''}
              data-depict-${format}-el="checkbox"
              data-filter-key="${filterData.key}"
              data-filter-op="${filterData.op}"
              data-filter-option-value="${filterOption.value}"
              ${filterOption.count === 0 ? 'disabled' : ''}
            >
            <label
              class="drw-Filters_Checkbox fz-12_155${filterData.key === 'color_name' ? ' drw-Filters_Checkbox-color' : ''}"
              style="${filterData.key === 'color_name' ? `--Swatch_Color: ${Depict.getColourHex(filterOption.value)};` : ''}"
              for="${filterData.key}-${filterOption.value}"
            >${filterOption.name}</label>
          </li>
        `
      })

      const checkIsExpanded = expandedFilters.find(
        (filterGroup) => filterGroup.key === filterData.key,
      )

      return `
        <fieldset
          class="drw-Filters_Group ${checkIsExpanded ? 'drw-Filters_Group-expanded' : ''}"
          data-key="${filterData.key}"
          data-depict-${format}-el="group"
        >
          <legend class="drw-Filters_Header">
            <p class="drw-Filters_Heading fz-12_155">
              ${filterData.name}
            </p>
          </legend>

          <ul class="drw-Filters_Options">
            ${filterOptions.filter(Boolean).join('')}
          </ul>

          ${
            filterOptions.length > 6
              ? `<button
                type="button"
                class="drw-Filters_MoreButton fz-12_155"
                data-depict-${format}-el="more-toggle"
                data-key="${filterData.key}"
              >
                <span class="drw-Filters_MoreText drw-Filters_MoreText-more">
                  + ${filterOptions.length - 6} ${window.theme.strings.more}
                </span>

                <span class="drw-Filters_MoreText drw-Filters_MoreText-less">
                  - ${filterOptions.length - 6} ${window.theme.strings.less}
                </span>
              </button>
            `
              : ''
          }
        </fieldset>
      `
    })

    return filtersHTML?.filter(Boolean).join('')
  }

  static getColourHex (colourValue) {
    const matchedColour = window.theme.colours.find(
      (colour) => colour.name === colourValue,
    )

    return matchedColour ? matchedColour.color : 'transparent'
  }
}

window.BAO.Depict = Depict
